import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Bell, KeyRound, Search, ShoppingCart } from "lucide-react";
import { useAuth } from "../../stores/auth";
import { api } from "../../lib/api";
import { credits as fmtCredits } from "../../lib/format";
import { SearchTrigger } from "./CommandPalette";
import { desktopNavItems } from "../../config/storefront-nav";
import { cartBadgeCount, cartNavAriaLabel, cartNavPath, formatCartBadge } from "../../lib/cart-badge";

interface AppHeaderProps {
  unread: number;
  cartCount: number;
  pendingPaymentCount: number;
  notifOpen: boolean;
  onToggleNotif: () => void;
  onOpenSearch: () => void;
}

/**
 * Mobile: logo + search + notifications + profile chip.
 * Desktop: page title, search bar, profile chip, utilities.
 */
export function AppHeader({
  unread,
  cartCount,
  pendingPaymentCount,
  notifOpen,
  onToggleNotif,
  onOpenSearch,
}: AppHeaderProps) {
  const { user, config, moduleEnabled } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  const { data: wallet } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ balance: number }>("/wallet"),
    enabled: !!user && !!config?.creditsEnabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const title =
    desktopNavItems(moduleEnabled, !!user, config).find((item) =>
      item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to),
    )?.label ?? "Mr Gaole";

  const displayName = user?.displayName?.trim() || user?.email.split("@")[0] || "Collector";
  const creditBalance = wallet?.balance;
  const showSearch = moduleEnabled("ecommerce") || moduleEnabled("packs") || moduleEnabled("raffles") || moduleEnabled("auctions");
  const cartBadge = cartBadgeCount(cartCount, pendingPaymentCount);

  const profileChip = user ? (
    <Link
      to="/account"
      className="flex max-w-[148px] items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1.5 transition-colors hover:border-brand-pink/40 sm:max-w-[200px]"
      aria-label="Profile"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-xs font-bold text-white">
        {displayName[0]?.toUpperCase()}
      </span>
      <span className="min-w-0 text-left leading-tight">
        <span className="block truncate text-xs font-semibold text-white/90">{displayName}</span>
        {config?.creditsEnabled ? (
          <span className="block truncate text-[10px] font-semibold tabular-nums text-brand-pink">
            {fmtCredits(creditBalance ?? 0)}
          </span>
        ) : (
          <span className="block text-[10px] text-white/45">Profile</span>
        )}
      </span>
    </Link>
  ) : (
    <Link
      to="/login"
      className="rounded-full bg-white/5 p-2 text-white/80 transition-colors hover:bg-white/10"
      aria-label="Sign in"
    >
      <KeyRound size={18} />
    </Link>
  );

  const utilities = (
    <div className="flex items-center gap-1.5">
      {showSearch && (
        <button
          type="button"
          onClick={onOpenSearch}
          className="rounded-full bg-white/5 p-2 text-white/80 transition-colors hover:bg-white/10 lg:hidden"
          aria-label="Search"
        >
          <Search size={18} />
        </button>
      )}
      {user && (
        <button
          type="button"
          onClick={onToggleNotif}
          className="relative rounded-full bg-white/5 p-2 text-white/80 transition-colors hover:bg-white/10"
          aria-label="Notifications"
          aria-expanded={notifOpen}
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-pink px-1 text-[9px] font-bold">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}
      {moduleEnabled("ecommerce") && user && (
        <Link
          to={cartNavPath(cartCount, pendingPaymentCount)}
          className="relative hidden rounded-full bg-white/5 p-2 text-white/80 transition-colors hover:bg-white/10 lg:flex"
          aria-label={cartNavAriaLabel(cartCount, pendingPaymentCount)}
        >
          <ShoppingCart size={18} />
          {cartBadge > 0 && (
            <motion.span
              key={cartBadge}
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                pendingPaymentCount > 0 ? "bg-amber-400 text-brand-dark" : "bg-brand-blue text-white"
              }`}
            >
              {formatCartBadge(cartBadge)}
            </motion.span>
          )}
        </Link>
      )}
      {profileChip}
    </div>
  );

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "border-b border-white/10 bg-brand-dark/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      {/* ---------- mobile ---------- */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 lg:hidden">
        <Link to="/" className="min-w-0 shrink">
          <motion.img
            src="/logo-wordmark.png"
            alt="Mr Gaole"
            animate={{ height: scrolled ? 26 : 32 }}
            className="w-auto max-w-[120px] shrink-0 object-contain object-left sm:max-w-[140px]"
          />
        </Link>
        <div className="flex items-center gap-1.5">{utilities}</div>
      </div>

      {/* ---------- desktop ---------- */}
      <div className="hidden items-center gap-4 px-6 py-3.5 lg:flex">
        <div className="min-w-0">
          <p className="font-display text-base font-bold leading-tight">{title}</p>
        </div>

        {showSearch && (
          <SearchTrigger
            onClick={onOpenSearch}
            className="ml-auto w-full max-w-sm px-3.5 py-2.5"
          />
        )}

        <div className={showSearch ? "" : "ml-auto"}>{utilities}</div>
      </div>
    </header>
  );
}
